import { useEffect, useMemo, useRef, useState } from 'react';

import { type ErrorKey, type ErrorMapping, getErrorMapping, getOrderedErrorKeys } from './errorMapping';

import type { GetUserPropertyDetailsResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';

export type ErrorGuideState = {
  errors: Array<ErrorKey>;
  currentErrorIndex: number;
  errorMessage: string;
  onJumpToError: () => void;
  onNext: () => void;
  onPrevious: () => void;
} | null;

type UseValidationErrorGuideOptions = {
  requiredFieldsErrors: Record<string, boolean>;
  currentProperty?: GetUserPropertyDetailsResponseWebDto;
  feeAmount: number | null;
  isRoomForRent: boolean;
  onErrorGuideChange?: (state: ErrorGuideState) => void;
  onNavigateToError: (mapping: ErrorMapping) => void;
};

const useValidationErrorGuide = ({
  requiredFieldsErrors,
  currentProperty: _currentProperty,
  feeAmount: _feeAmount,
  isRoomForRent,
  onErrorGuideChange,
  onNavigateToError,
}: UseValidationErrorGuideOptions) => {
  const [currentErrorIndex, setCurrentErrorIndex] = useState(0);
  const prevErrorKeysRef = useRef<Array<ErrorKey>>([]);

  const orderedErrorKeys = useMemo(() => getOrderedErrorKeys(requiredFieldsErrors), [requiredFieldsErrors]);

  const currentErrorMapping = useMemo(() => {
    if (orderedErrorKeys.length === 0 || currentErrorIndex >= orderedErrorKeys.length) {
      return null;
    }

    return getErrorMapping(orderedErrorKeys[currentErrorIndex], {
      isRoomForRent,
    });
  }, [orderedErrorKeys, currentErrorIndex, isRoomForRent]);

  // Clamp index when errors shrink
  useEffect(() => {
    if (currentErrorIndex >= orderedErrorKeys.length) {
      setCurrentErrorIndex(Math.max(0, orderedErrorKeys.length - 1));
    }
  }, [orderedErrorKeys.length, currentErrorIndex]);

  // Auto-advance to next error when current error is fixed
  useEffect(() => {
    const prevErrorKeys = prevErrorKeysRef.current;

    if (prevErrorKeys.length === 0 && orderedErrorKeys.length > 0) {
      prevErrorKeysRef.current = orderedErrorKeys;
      return;
    }

    if (prevErrorKeys.length > orderedErrorKeys.length && orderedErrorKeys.length > 0) {
      const currentErrorKey = prevErrorKeys[currentErrorIndex];
      const wasCurrentErrorResolved = currentErrorKey && !orderedErrorKeys.includes(currentErrorKey);

      if (wasCurrentErrorResolved) {
        // Keep the same index position - the next error now occupies this slot
        // This provides a smooth UX where users naturally progress through errors
        const newIndex = Math.min(currentErrorIndex, orderedErrorKeys.length - 1);
        setCurrentErrorIndex(newIndex);
      }
    }

    if (orderedErrorKeys.length === 0 && prevErrorKeys.length > 0) {
      setCurrentErrorIndex(0);
    }

    prevErrorKeysRef.current = orderedErrorKeys;
  }, [orderedErrorKeys, currentErrorIndex]);

  const handleJumpToError = () => {
    if (!currentErrorMapping) return;
    onNavigateToError(currentErrorMapping);
  };

  const handleNextError = () => {
    if (currentErrorIndex < orderedErrorKeys.length - 1) {
      setCurrentErrorIndex(currentErrorIndex + 1);
    }
  };

  const handlePreviousError = () => {
    if (currentErrorIndex > 0) {
      setCurrentErrorIndex(currentErrorIndex - 1);
    }
  };

  // Notify parent of error guide state changes
  const errorGuideStateKeyRef = useRef('');
  useEffect(() => {
    if (!onErrorGuideChange) return;

    const stateKey = `${orderedErrorKeys.join(',')}-${currentErrorIndex}-${currentErrorMapping?.componentKey}-${currentErrorMapping?.errorMessage}`;
    if (stateKey === errorGuideStateKeyRef.current) return;
    errorGuideStateKeyRef.current = stateKey;
    if (orderedErrorKeys.length > 0 && currentErrorMapping) {
      onErrorGuideChange({
        errors: orderedErrorKeys,
        currentErrorIndex,
        errorMessage: currentErrorMapping.errorMessage,
        onJumpToError: handleJumpToError,
        onNext: handleNextError,
        onPrevious: handlePreviousError,
      });
    } else {
      onErrorGuideChange(null);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [onErrorGuideChange, orderedErrorKeys, currentErrorIndex, currentErrorMapping]);

  return { orderedErrorKeys, currentErrorIndex, currentErrorMapping };
};

export default useValidationErrorGuide;
